import {
  useListUpgradeAssessmentStages,
  useListUpgradePlanStages,
  useListUpgradeTemplateStages,
} from 'api/frontend';
import { UpgradeStage, UpgradeStageStep, UpgradeTemplate } from 'api/models';
import useUserAccountState from 'hooks/useUserAccountState';
import { UPGRADE_STAGES } from 'src/data/upgrades';
import { Avatar, Badge } from 'subframe/index';
import styles from './StepSidebar.module.scss';
import StepDrawerCommentsForChkk from './StepDrawerCommentsForChkk';
import StepDrawerCommentsForTeam from './StepDrawerCommentsForTeam';
import { StepActivityFeed } from './StepActivityFeed';
import { CommentTooltip } from 'components/design-system/CommentTooltip';
import { Divider } from 'components/design-system';
import { UpgradeAny, UpgradeViewMode } from '../helpers/UpgradesHelpers';
import { KeyedMutator } from 'swr';

interface UpgradeStepSidebarProps {
  upgrade: UpgradeAny;
  mode: UpgradeViewMode;
  stage: UpgradeStage;
  step: UpgradeStageStep;
  showExampleData: boolean;
}

export function UpgradeStepSidebar(props: UpgradeStepSidebarProps) {
  const { user, account } = useUserAccountState();

  // TODO (laiba): fix DrawerProvider context handling, to avoid processing stages data again for threadId,
  // (had to do it since Sidebar doesn't re-renders on /stages mutate in main UpgradesView)
  let upgradeStagesResponse: {
    data: { data: UpgradeStage[] } | undefined;
    mutate: KeyedMutator<any> | undefined;
  } = { data: undefined, mutate: undefined };
  switch (props.mode) {
    case UpgradeViewMode.Plan:
      upgradeStagesResponse = useListUpgradePlanStages(
        props.upgrade.id || '',
        { page_size: 100 },
        {
          request: { headers: { Authorization: `Bearer ${account.token}` } },
          swr: {
            enabled: !props.showExampleData,
          },
        },
      );
      break;
    case UpgradeViewMode.Template:
      upgradeStagesResponse = useListUpgradeTemplateStages(
        props.upgrade.id || '',
        { page_size: 100 },
        {
          request: { headers: { Authorization: `Bearer ${account.token}` } },
          swr: {
            enabled: !props.showExampleData,
          },
        },
      );
      break;
    case UpgradeViewMode.Assessment:
      upgradeStagesResponse = useListUpgradeAssessmentStages(
        props.upgrade.id || '',
        { n: 100 },
        {
          request: { headers: { Authorization: `Bearer ${account.token}` } },
          swr: {
            enabled: !props.showExampleData,
          },
        },
      );
      break;
  }
  const stages = (
    (props.showExampleData
      ? UPGRADE_STAGES.get(props.upgrade.id || '')
      : upgradeStagesResponse.data?.data) || []
  ).sort((a, b) => a.stage_number - b.stage_number);
  const currentStage = stages.find(
    (stage) => stage.stage_number === props.stage.stage_number,
  );
  const currentStep = currentStage?.steps.find(
    (step) => step.step_number === props.step.step_number,
  );
  const stepCommentThreadId = currentStep?.comments.thread_id || '';

  return (
    <div className={styles['stack-d78aa573']}>
      <div className="flex items-start gap-2 px-1 py-1">
        <Avatar image={user.profilePicture} />
        <span className="text-subheader font-subheader text-default-font ph-no-capture">
          {`${user.name} : ${user.email}`}
        </span>
      </div>
      <Divider />
      <div className="flex w-full items-start gap-2">
        <div className="flex items-center gap-2 pt-4 pb-2">
          <Avatar variant="neutral">
            {currentStep?.step_number.toString()}
          </Avatar>
          <span className="text-subheader font-subheader text-default-font">
            {currentStep?.name}
          </span>
        </div>
        {currentStep?.status === 'not-marked-for-upgrade' && (
          <div className="flex grow shrink-0 basis-0 items-start gap-2 pt-[22px]">
            <CommentTooltip
              userName={currentStep?.last_status_change_details?.user?.name}
              picture={currentStep?.last_status_change_details?.user?.picture}
              comment={currentStep?.last_status_change_details?.comment || ''}
              time={currentStep?.last_status_change_details?.changed}
              action="marked this step as 'Don't Upgrade'"
            >
              <Badge variant="neutral">Not Marked for Upgrade</Badge>
            </CommentTooltip>
          </div>
        )}
        {currentStep?.status === 'marked-for-upgrade' && (
          <div className="flex grow shrink-0 basis-0 items-start gap-2 pt-[22px]">
            <CommentTooltip
              userName={currentStep?.last_status_change_details?.user?.name}
              picture={currentStep?.last_status_change_details?.user?.picture}
              comment={''}
              time={currentStep?.last_status_change_details?.changed}
              action="marked this step as 'For Upgrade'"
            >
              <Badge variant="brand">Marked for Upgrade</Badge>
            </CommentTooltip>
          </div>
        )}
      </div>
      <StepActivityFeed
        mode={props.mode}
        upgrade={props.upgrade}
        stage={props.stage}
        step={props.step}
        showExampleData={props.showExampleData}
      />
      <StepDrawerCommentsForTeam
        mode={props.mode}
        upgrade={props.upgrade}
        stage={props.stage}
        step={props.step}
        showExampleData={props.showExampleData}
        commentThreadId={stepCommentThreadId}
        onChange={() => {
          if (upgradeStagesResponse.mutate) {
            upgradeStagesResponse.mutate();
          }
        }}
      />
      {(props.mode === UpgradeViewMode.Template ||
        props.mode === UpgradeViewMode.Assessment) && (
        <StepDrawerCommentsForChkk
          mode={props.mode}
          upgrade={props.upgrade}
          stage={props.stage}
          step={props.step}
          showExampleData={props.showExampleData}
          commentThreadId={stepCommentThreadId}
          onChange={() => {
            if (upgradeStagesResponse.mutate) {
              upgradeStagesResponse.mutate();
            }
          }}
        />
      )}
    </div>
  );
}
